import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import { 
  Security, 
  Notifications, 
  PrivacyTip, 
  Email,
  Lock,
  ChevronRight,
  Delete,
} from '@mui/icons-material';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import { useAuth } from '../../hooks/AuthProvider';
import { Button } from '../Button/Button';

interface AccountSettingsCardProps {
  className?: string;
}

const AccountSettingsCard: React.FC<AccountSettingsCardProps> = ({ className }) => {
  const { deleteAccount } = useAuth();
  const [emailNotifications, setEmailNotifications] = React.useState(true);
  const [pushNotifications, setPushNotifications] = React.useState(false);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');

  const handleDeleteAccount = async () => {
    if (confirmText.toLowerCase() !== 'delete my account') return;
    setIsDeleting(true);
    setErrorMsg('');
    try {
      await deleteAccount();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Account deletion failed');
      setIsDeleting(false);
    }
  };

  const handleSettingClick = (setting: string) => {
    console.log(`Opening ${setting} settings...`);
    // TODO: Implement navigation to specific settings pages
  };

  return (
    <GlassmorphicCard className={`account-settings-card ${className || ''}`}>
      <Box p={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <Security 
            sx={{ 
              fontSize: 28, 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'transparent',
              backgroundClip: 'text',
              mr: 1.5,
            }} 
          />
          <Typography variant="h6" fontWeight={600}>
            Account Settings
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Settings List */}
        <List disablePadding>
          {/* Security Settings */}
          {/* Temporarily commented out - Security & Privacy section
          <ListItemButton 
            onClick={() => handleSettingClick('security')}
            sx={{
              borderRadius: 1,
              mb: 1,
              '&:hover': {
                backgroundColor: 'rgba(240, 110, 245, 0.1)',
              },
            }}
          >
            <ListItemIcon>
              <Lock sx={{ color: '#f06ef5' }} />
            </ListItemIcon>
            <ListItemText
              primary="Security & Privacy"
              secondary="Password, two-factor authentication, privacy settings"
            />
            <ChevronRight sx={{ color: 'text.secondary' }} />
          </ListItemButton>
          */}

          {/* Email Notifications */}
          <ListItem
            sx={{
              borderRadius: 1,
              mb: 1,
            }}
          >
            <ListItemIcon>
              <Email sx={{ color: '#91EAE4' }} />
            </ListItemIcon>
            <ListItemText
              primary="Email Notifications"
              secondary="Receive updates about plans and reservations"
            />
            <Switch
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#f06ef5',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#f06ef5',
                },
              }}
            />
          </ListItem>

          {/* Push Notifications */}
          <ListItem
            sx={{
              borderRadius: 1,
              mb: 1,
            }}
          >
            <ListItemIcon>
              <Notifications sx={{ color: '#fda085' }} />
            </ListItemIcon>
            <ListItemText
              primary="Push Notifications"
              secondary="Real-time updates and reminders"
            />
            <Switch
              checked={pushNotifications}
              onChange={(e) => setPushNotifications(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#f06ef5',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#f06ef5',
                },
              }}
            />
          </ListItem>

          {/* Privacy Settings */}
          {/* Temporarily commented out - Data & Privacy section
          <ListItemButton 
            onClick={() => handleSettingClick('privacy')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(240, 110, 245, 0.1)',
              },
            }}
          >
            <ListItemIcon>
              <PrivacyTip sx={{ color: '#9c88ff' }} />
            </ListItemIcon>
            <ListItemText
              primary="Data & Privacy"
              secondary="Control how your data is used and shared"
            />
            <ChevronRight sx={{ color: 'text.secondary' }} />
          </ListItemButton>
          */}
        </List>

        <Divider sx={{ my: 2 }} />
        
        <ListItemButton 
          onClick={() => setDeleteDialogOpen(true)}
          sx={{
            borderRadius: 1,
            backgroundColor: 'rgba(255, 59, 48, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(255, 59, 48, 0.15)',
            },
          }}
        >
          <ListItemIcon>
            <Delete sx={{ color: '#ff3b30' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography color="error.main" fontWeight={600}>Delete Account</Typography>}
            secondary="Permanently delete your profile and all associated data"
          />
          <ChevronRight sx={{ color: 'error.main' }} />
        </ListItemButton>

        {/* Help text */}
        <Box 
          mt={2}
          p={2}
          sx={{
            backgroundColor: 'rgba(103, 126, 234, 0.1)',
            borderRadius: 1,
            border: '1px solid rgba(103, 126, 234, 0.2)',
          }}
        >
          <Typography variant="body2" color="info.main">
            💡 Tip: Keep notifications enabled to stay updated on plan changes and reservation confirmations.
          </Typography>
        </Box>

        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => !isDeleting && setDeleteDialogOpen(false)}
          PaperProps={{
            sx: {
              background: 'rgba(30, 30, 40, 0.85)',
              backdropFilter: 'blur(25px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#fff',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Delete Account permanently?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
              Deleting your account is permanent. This will anonymize your profile, cancel your active subscriptions, and drop all plan invitations and details. You have a 30-day grace period to restore your account before data is permanently deleted.
            </DialogContentText>
            <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontWeight: 600 }}>
              To confirm, please type "delete my account" below:
            </DialogContentText>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="delete my account"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isDeleting}
              InputProps={{
                sx: {
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.4)',
                  },
                }
              }}
            />
            {errorMsg && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errorMsg}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              intent="ghost"
              disabled={isDeleting}
              onClick={() => {
                setConfirmText('');
                setDeleteDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              intent="danger"
              disabled={confirmText.toLowerCase() !== 'delete my account' || isDeleting}
              loading={isDeleting}
              onClick={handleDeleteAccount}
            >
              Delete My Account
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GlassmorphicCard>
  );
};

export default AccountSettingsCard;